import { useEffect, useState } from 'react';

// Shared presentation + data-loading helpers for the console panels.

// pg timestamptz text → 'YYYY-MM-DD HH:MM'.
export const ts = (s: string | null) => (s ? s.slice(0, 16) : '—');

// USD, matching the commerce storefront's formatting (so the same numbers read identically here).
export const money = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// snake_case status → 'Snake Case' for display (null/undefined → em dash).
export const label = (s: string | null | undefined) => (s ?? '—').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Unit price — 2–4 decimals (per-each prices are often sub-cent), matching the storefront.
export const unit = (n: number | null | undefined) =>
  n == null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;

// Debounce a rapidly-changing value (search boxes) so we don't refetch on every keystroke.
export function useDebounced<T>(value: T, ms = 200): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// Load-once-per-deps with an error slot and a manual reload — the read-panel pattern in one place.
// `fn` is intentionally excluded from the dependency list (it's a fresh closure each render); pass
// anything the load depends on via `deps`.
export function useLoad<T>(
  fn: () => Promise<T>,
  deps: unknown[] = [],
): { data: T | null; err: string | null; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let live = true;
    setData(null);
    setErr(null);
    fn()
      .then((d) => live && setData(d))
      .catch((e) => live && setErr(String(e?.message ?? e)));
    return () => {
      live = false;
    };
  }, [...deps, nonce]);
  return { data, err, reload: () => setNonce((n) => n + 1) };
}

// "Login as camp": the storefront, opened in a new tab acting for one COMMERCE camp (a commerce
// org id — never the platform registry's uuid). The link only asks. The storefront reads `?actAs=`
// and runs its own camp-picker flow under the staff member's own session (commerce POST
// /api/ops/act-as), where the act-as rule and its audit trail live, and shows its own refusal to
// anyone it refuses. It never goes through this BFF's service key (/api/svc): that path has no real
// person behind it to audit. VITE_STOREFRONT_URL exists only to point a local build at a local
// storefront.
export const STOREFRONT_URL = ((import.meta.env.VITE_STOREFRONT_URL as string | undefined) || 'https://order.tavezio.io').replace(/\/+$/, '');
/** The storefront's host, for sentences that tell someone where to sign in. */
export const STOREFRONT_HOST = new URL(STOREFRONT_URL).host;

export function LoginAsCamp({ orgId, name }: { orgId: number | null | undefined; name?: string | null }) {
  if (orgId == null) return <span className="muted" title="No commerce camp is linked to this account">—</span>;
  return (
    <a className="btn-sm ghost login-as" href={`${STOREFRONT_URL}/?actAs=${orgId}`} target="_blank" rel="noopener noreferrer"
      title={`Open the storefront as ${name ?? `camp ${orgId}`} in a new tab`}>Login as camp ↗</a>
  );
}
